import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import Sidebar from "../components/Sidebar";
import API from "../APIs/API";
import "../styles/list.scss";

const CATEGORIES = ["Executive", "Wing", "Division", "Branch", "Unit"];

const CATEGORY_STYLES = {
  Executive: { bg: "#FFF8E1", border: "#F57F17", label: "#F57F17" },
  Wing:      { bg: "#E3F2FD", border: "#1565C0", label: "#1565C0" },
  Division:  { bg: "#E8F5E9", border: "#2E7D32", label: "#2E7D32" },
  Branch:    { bg: "#FFF3E0", border: "#E65100", label: "#E65100" },
  Unit:      { bg: "#F3E5F5", border: "#6A1B9A", label: "#6A1B9A" },
};

const AdminLocationUsers = ({ type }) => {
  const { admin_location_id } = useParams();
  const pageUserType = type === "Non-Teacher" ? "non-teachers" : "teachers";

  // ── Admin location description ───────────────────────────────────────────
  const [adminDesc, setAdminDesc] = useState("");

  useEffect(() => {
    new API().getAdminLocationById(admin_location_id).then((res) => {
      if (res && res.data) setAdminDesc(res.data.admin_desc || "");
    });
  }, [admin_location_id]);

  // ── Org chart items (DB-backed) ──────────────────────────────────────────
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);

  const loadItems = () => {
    setLoadingItems(true);
    new API().getOrgNodes(admin_location_id).then((res) => {
      if (res && res.data) {
        setItems(res.data.map((n) => ({ id: n.id, name: n.name, category: n.type })));
      }
      setLoadingItems(false);
    });
  };

  useEffect(() => { loadItems(); }, [admin_location_id]); // eslint-disable-line

  // ── Modal state ──────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen]           = useState(false);
  const [activeCategory, setActiveCategory] = useState("");
  const [inputValue, setInputValue]         = useState("");
  const [saving, setSaving]                 = useState(false);

  const handleOpenModal = (category) => {
    setActiveCategory(category);
    setInputValue("");
    setModalOpen(true);
  };

  const handleCancel = () => {
    setModalOpen(false);
    setInputValue("");
  };

  const handleSave = async () => {
    if (!inputValue.trim()) return;
    setSaving(true);
    const res = await new API().addOrgNode({
      admin_location_id,
      name: inputValue.trim(),
      type: activeCategory,
    });
    if (res && res.data) {
      setItems((prev) => [
        ...prev,
        { id: res.data.id, name: res.data.name, category: res.data.type },
      ]);
    }
    setSaving(false);
    setModalOpen(false);
    setInputValue("");
  };

  const handleDelete = async (id) => {
    await new API().deleteOrgNode(id);
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  return (
    <div className="wrapper">
      {/* ── Sidebar ── */}
      <div className="sidebar">
        <Sidebar />
      </div>

      {/* ── Main content ── */}
      <div className="content">
        <div className="list">
          <div className="listContainer">
            <div className="datatable">

              {/* Back button */}
              <Link to={`/${pageUserType}`} className="backButtonSection">
                <KeyboardBackspaceIcon className="backButton" />
              </Link>
              <br />
              <br />

              {/* Title row + Add buttons */}
              <div className="datatableTitle">
                {adminDesc ? `Organisational Chart for ${adminDesc}` : "Organisational Chart"}
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat}
                      className="addBtn"
                      onClick={() => handleOpenModal(cat)}
                    >
                      Add {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Display area ── */}
              <div style={{ marginTop: "24px" }}>
                {loadingItems ? (
                  <div style={{ textAlign: "center", padding: "40px", color: "#aaa" }}>
                    Loading…
                  </div>
                ) : (
                  <>
                    {CATEGORIES.map((cat) => {
                      const catItems = items.filter((item) => item.category === cat);
                      if (catItems.length === 0) return null;
                      const s = CATEGORY_STYLES[cat];
                      return (
                        <div key={cat} style={{ marginBottom: "28px" }}>
                          {/* Category heading */}
                          <h4 style={{
                            color: s.label,
                            marginBottom: "12px",
                            fontSize: "15px",
                            fontWeight: "700",
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                            borderBottom: `2px solid ${s.border}`,
                            paddingBottom: "6px",
                          }}>
                            {cat}
                          </h4>

                          {/* Cards grid */}
                          <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                            gap: "14px",
                          }}>
                            {catItems.map((item) => (
                              <div
                                key={item.id}
                                style={{
                                  backgroundColor: s.bg,
                                  border: `2px solid ${s.border}`,
                                  borderRadius: "10px",
                                  padding: "14px 18px 12px",
                                  position: "relative",
                                  boxShadow: "0 2px 6px rgba(0,0,0,0.07)",
                                }}
                              >
                                {/* Delete button */}
                                <button
                                  onClick={() => handleDelete(item.id)}
                                  title="Remove"
                                  style={{
                                    position: "absolute",
                                    top: "6px",
                                    right: "8px",
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    color: "#aaa",
                                    fontSize: "14px",
                                    lineHeight: 1,
                                    padding: "0 2px",
                                  }}
                                >
                                  ✕
                                </button>

                                {/* Category label badge */}
                                <span style={{
                                  display: "inline-block",
                                  backgroundColor: s.border,
                                  color: "#fff",
                                  fontSize: "10px",
                                  fontWeight: "700",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.6px",
                                  borderRadius: "4px",
                                  padding: "2px 7px",
                                  marginBottom: "8px",
                                }}>
                                  {cat}
                                </span>

                                {/* Entry name */}
                                <p style={{
                                  margin: 0,
                                  fontSize: "15px",
                                  fontWeight: "600",
                                  color: "#222",
                                  wordBreak: "break-word",
                                  paddingRight: "14px",
                                }}>
                                  {item.name}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}

                    {/* Empty state */}
                    {items.length === 0 && (
                      <div style={{
                        textAlign: "center",
                        padding: "50px 20px",
                        color: "#bbb",
                        fontSize: "14px",
                        border: "2px dashed #e0e0e0",
                        borderRadius: "10px",
                        marginTop: "16px",
                      }}>
                        No entries yet. Use the buttons above to add Executives, Wings, Divisions, Branches or Units.
                      </div>
                    )}
                  </>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* ── Modal ── */}
      {modalOpen && (
        <div style={{
          position: "fixed",
          top: 0, left: 0,
          width: "100%", height: "100%",
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: "#fff",
            borderRadius: "14px",
            padding: "36px",
            width: "420px",
            boxShadow: "0 16px 50px rgba(0,0,0,0.22)",
          }}>
            {/* Modal title */}
            <h3 style={{ margin: "0 0 6px", color: "#1F4E79", fontSize: "20px" }}>
              Add New {activeCategory}
            </h3>
            <p style={{ margin: "0 0 20px", color: "#888", fontSize: "13px" }}>
              Enter a name for the new {activeCategory.toLowerCase()}.
            </p>

            {/* Input */}
            <input
              type="text"
              placeholder={`Enter ${activeCategory} name…`}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !saving) handleSave(); }}
              autoFocus
              disabled={saving}
              style={{
                width: "100%",
                padding: "10px 14px",
                fontSize: "14px",
                border: "1.5px solid #ccc",
                borderRadius: "8px",
                boxSizing: "border-box",
                marginBottom: "22px",
                outline: "none",
              }}
            />

            {/* Buttons */}
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={handleCancel}
                disabled={saving}
                style={{
                  padding: "9px 22px",
                  border: "1.5px solid #ccc",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  cursor: "pointer",
                  fontSize: "14px",
                  color: "#555",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !inputValue.trim()}
                style={{
                  padding: "9px 22px",
                  border: "none",
                  borderRadius: "8px",
                  backgroundColor: saving ? "#90aec6" : "#1F4E79",
                  color: "#fff",
                  cursor: saving ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLocationUsers;
